import { Chess } from 'chess.js';

import { UciMessageParser, type ParsedUciMessage } from './uciParser';
import {
  StockfishClientError,
  type StockfishBestMoveResult,
  type StockfishCancelledResult,
  type StockfishClientOptions,
  type StockfishPrincipalVariation,
  type StockfishSearchOutcome,
  type StockfishSearchRequest,
  type StockfishWorkerFactory,
  type StockfishWorkerLike,
} from './types';

const DEFAULT_HANDSHAKE_TIMEOUT_MS = 10_000;
const DEFAULT_SEARCH_TIMEOUT_MS = 30_000;
const DEFAULT_STOP_TIMEOUT_MS = 1_000;
const DEFAULT_HASH_MB = 16;

type WorkerPhase = 'none' | 'uci' | 'ready-check' | 'idle' | 'searching' | 'stopping';

interface Deferred<T> {
  readonly promise: Promise<T>;
  readonly resolve: (value: T | PromiseLike<T>) => void;
  readonly reject: (reason?: unknown) => void;
}

interface PendingSearch {
  readonly request: StockfishSearchRequest;
  readonly deferred: Deferred<StockfishSearchOutcome>;
  readonly principalVariations: Map<number, StockfishPrincipalVariation>;
  settled: boolean;
  cancelled: boolean;
  recoveryAttempted: boolean;
}

/**
 * Owns exactly one lazy Stockfish Worker and serializes all UCI searches.
 * Stockfish only proposes moves; the injected validator remains the rules gate.
 */
export class StockfishClient {
  private readonly createWorker: StockfishWorkerFactory;
  private readonly validateBestMove: StockfishClientOptions['validateBestMove'];
  private readonly hashMb: number;
  private readonly handshakeTimeoutMs: number;
  private readonly searchTimeoutMs: number;
  private readonly stopTimeoutMs: number;
  private worker: StockfishWorkerLike | null = null;
  private workerPhase: WorkerPhase = 'none';
  private parser = new UciMessageParser();
  private initialization: Deferred<void> | null = null;
  private initializationRecoveryAttempted = false;
  private currentSearch: PendingSearch | null = null;
  private queuedSearch: PendingSearch | null = null;
  private handshakeTimer: number | null = null;
  private searchTimer: number | null = null;
  private stopTimer: number | null = null;
  private lastSearchId = -1;
  private destroyed = false;

  public constructor(options: StockfishClientOptions) {
    this.createWorker = options.createWorker;
    this.validateBestMove = options.validateBestMove;
    this.hashMb = positiveSafeInteger(options.hashMb, DEFAULT_HASH_MB, 'hashMb');
    this.handshakeTimeoutMs = positiveTimeout(
      options.handshakeTimeoutMs,
      DEFAULT_HANDSHAKE_TIMEOUT_MS,
      'handshakeTimeoutMs',
    );
    this.searchTimeoutMs = positiveTimeout(
      options.searchTimeoutMs,
      DEFAULT_SEARCH_TIMEOUT_MS,
      'searchTimeoutMs',
    );
    this.stopTimeoutMs = positiveTimeout(
      options.stopTimeoutMs,
      DEFAULT_STOP_TIMEOUT_MS,
      'stopTimeoutMs',
    );
  }

  /** Lazily creates the Worker and completes the pinned UCI handshake. */
  public initialize(): Promise<void> {
    if (this.destroyed) {
      return Promise.reject(new StockfishClientError('destroyed', 'Stockfish client is destroyed.'));
    }
    if (this.isWorkerReady()) {
      return Promise.resolve();
    }
    if (this.initialization) {
      return this.initialization.promise;
    }

    this.initialization = createDeferred<void>();
    this.initializationRecoveryAttempted = false;
    this.startWorker();
    return this.initialization.promise;
  }

  /**
   * Starts one revision-bound search. A newer ID supersedes queued/current work,
   * sends `stop`, waits for its terminal `bestmove`, retires that Worker, and
   * completes a fresh handshake before issuing replacement `go`.
   */
  public search(request: StockfishSearchRequest): Promise<StockfishSearchOutcome> {
    if (this.destroyed) {
      return Promise.reject(new StockfishClientError('destroyed', 'Stockfish client is destroyed.'));
    }

    try {
      request = validateSearchRequest(request, this.lastSearchId);
    } catch (error) {
      return Promise.reject(error);
    }

    this.lastSearchId = request.searchId;
    const pending = createPendingSearch(request);

    if (this.queuedSearch) {
      this.cancelPending(this.queuedSearch, 'superseded');
    }
    this.queuedSearch = pending;

    if (this.currentSearch) {
      this.cancelPending(this.currentSearch, 'superseded');
      this.beginStoppingCurrentSearch();
    } else if (this.isWorkerReady()) {
      this.launchQueuedSearch();
    } else {
      void this.initialize().catch(() => undefined);
    }

    return pending.deferred.promise;
  }

  /** Cancels a matching search, or all current/queued work when no ID is supplied. */
  public cancel(searchId?: number): void {
    if (this.queuedSearch && (searchId === undefined || this.queuedSearch.request.searchId === searchId)) {
      this.cancelPending(this.queuedSearch, 'cancelled');
      this.queuedSearch = null;
    }

    if (this.currentSearch && (searchId === undefined || this.currentSearch.request.searchId === searchId)) {
      this.cancelPending(this.currentSearch, 'cancelled');
      this.beginStoppingCurrentSearch();
    }
  }

  /** Idempotently cancels work and terminates only this client's Worker. */
  public destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    if (this.queuedSearch) {
      this.cancelPending(this.queuedSearch, 'destroyed');
      this.queuedSearch = null;
    }
    if (this.currentSearch) {
      this.cancelPending(this.currentSearch, 'destroyed');
      this.currentSearch = null;
    }

    const error = new StockfishClientError('destroyed', 'Stockfish client was destroyed.');
    this.rejectInitialization(error);
    this.terminateWorker();
  }

  private isWorkerReady(): boolean {
    return this.worker !== null && (
      this.workerPhase === 'idle' ||
      this.workerPhase === 'searching' ||
      this.workerPhase === 'stopping'
    );
  }

  private startWorker(): void {
    if (this.destroyed) {
      return;
    }

    let worker: StockfishWorkerLike;
    try {
      worker = this.createWorker();
    } catch (error) {
      this.handleWorkerFailure(
        new StockfishClientError('worker-error', 'Stockfish Worker creation failed.', { cause: error }),
      );
      return;
    }

    this.worker = worker;
    this.workerPhase = 'uci';
    this.parser = new UciMessageParser();
    worker.onmessage = (event) => {
      if (this.worker !== worker) {
        return;
      }
      this.handleWorkerMessage(event.data);
    };
    worker.onerror = (event) => {
      if (this.worker !== worker) {
        return;
      }
      const cause = event.error;
      const details = event.message ? `: ${event.message}` : '';
      this.handleWorkerFailure(
        new StockfishClientError('worker-error', `Stockfish Worker failed${details}`, { cause }),
      );
    };

    this.startHandshakeTimer();
    this.postCommands(['uci']);
  }

  private handleWorkerMessage(data: unknown): void {
    if (typeof data !== 'string') {
      this.handleWorkerFailure(
        new StockfishClientError('malformed-output', 'Stockfish Worker returned non-text output.'),
        false,
      );
      return;
    }

    if (this.processParsedMessages(this.parser.pushFrame(data))) {
      this.parser.reset();
    }
  }

  /** A terminal line is a protocol barrier; later lines in its batch are stale. */
  private processParsedMessages(messages: readonly ParsedUciMessage[]): boolean {
    for (const message of messages) {
      this.handleParsedMessage(message);
      if (message.type === 'bestmove' || message.type === 'malformed') {
        return true;
      }
    }

    return false;
  }

  private handleParsedMessage(message: ParsedUciMessage): void {
    switch (message.type) {
      case 'uciok':
        this.handleUciOk();
        return;
      case 'readyok':
        this.handleReadyOk();
        return;
      case 'info':
        this.recordPrincipalVariation(message);
        return;
      case 'bestmove':
        this.handleBestMove(message.move, message.ponder);
        return;
      case 'malformed':
        this.handleMalformedOutput(message.reason, message.raw);
        return;
      case 'other':
        return;
    }
  }

  private handleUciOk(): void {
    if (this.workerPhase !== 'uci') {
      return;
    }

    this.workerPhase = 'ready-check';
    this.startHandshakeTimer();
    this.postCommands([
      'setoption name Threads value 1',
      `setoption name Hash value ${this.hashMb}`,
      'setoption name Ponder value false',
      'setoption name UCI_LimitStrength value false',
      'setoption name Skill Level value 20',
      'isready',
    ]);
  }

  private handleReadyOk(): void {
    if (this.workerPhase !== 'ready-check') {
      return;
    }

    this.clearHandshakeTimer();
    this.workerPhase = 'idle';
    this.initializationRecoveryAttempted = false;
    const initialization = this.initialization;
    this.initialization = null;
    initialization?.resolve(undefined);
    this.launchQueuedSearch();
  }

  private launchQueuedSearch(): void {
    if (
      this.destroyed ||
      this.workerPhase !== 'idle' ||
      this.worker === null ||
      this.currentSearch !== null ||
      this.queuedSearch === null
    ) {
      return;
    }

    const pending = this.queuedSearch;
    this.queuedSearch = null;
    if (pending.cancelled) {
      this.launchQueuedSearch();
      return;
    }

    this.currentSearch = pending;
    this.workerPhase = 'searching';
    this.startSearchTimer();
    const request = pending.request;
    const goLimits = [
      request.depth === undefined ? null : `depth ${request.depth}`,
      request.nodes === undefined ? null : `nodes ${request.nodes}`,
      request.moveTimeMs === undefined ? null : `movetime ${request.moveTimeMs}`,
    ].filter((limit): limit is string => limit !== null);

    this.postCommands([
      `setoption name MultiPV value ${request.multiPv ?? 1}`,
      `position fen ${request.fen}`,
      `go ${goLimits.join(' ')}`,
    ]);
  }

  private beginStoppingCurrentSearch(): void {
    if (
      this.currentSearch === null ||
      this.worker === null ||
      this.workerPhase === 'stopping'
    ) {
      return;
    }
    if (this.workerPhase !== 'searching') {
      return;
    }

    this.clearSearchTimer();
    this.workerPhase = 'stopping';
    this.startStopTimer();
    this.postCommands(['stop']);
  }

  private handleBestMove(bestMove: string | null, ponder: string | null): void {
    const pending = this.currentSearch;
    if (!pending || (this.workerPhase !== 'searching' && this.workerPhase !== 'stopping')) {
      return;
    }

    const wasCancelled = pending.cancelled || this.workerPhase === 'stopping';
    this.clearSearchTimer();
    this.clearStopTimer();
    this.currentSearch = null;

    if (wasCancelled) {
      // A fresh Worker generation is the only reliable correlation boundary:
      // UCI bestmove lines carry no CapyChess search identifier.
      this.terminateWorker();
      if (this.queuedSearch) {
        this.restartWorkerForPendingSearch();
      }
      return;
    }

    this.workerPhase = 'idle';

    let isLegal = false;
    try {
      isLegal = this.validateBestMove({ request: pending.request, bestMove });
    } catch (error) {
      this.rejectPending(
        pending,
        new StockfishClientError(
          'illegal-bestmove',
          `Best move validation threw for search ${pending.request.searchId}.`,
          { cause: error },
        ),
      );
      this.launchQueuedSearch();
      return;
    }

    if (!isLegal) {
      this.rejectPending(
        pending,
        new StockfishClientError(
          'illegal-bestmove',
          `Stockfish proposed an illegal move for search ${pending.request.searchId}: ${bestMove}`,
        ),
      );
      this.launchQueuedSearch();
      return;
    }

    const result: StockfishBestMoveResult = {
      ...pending.request,
      status: 'bestmove',
      bestMove,
      ponder,
      principalVariations: Object.freeze(
        [...pending.principalVariations.values()].sort((left, right) => left.multiPv - right.multiPv),
      ),
    };
    pending.settled = true;
    pending.deferred.resolve(result);
    this.launchQueuedSearch();
  }

  private recordPrincipalVariation(message: Extract<ParsedUciMessage, { type: 'info' }>): void {
    const pending = this.currentSearch;
    if (!pending || pending.cancelled || this.workerPhase !== 'searching' || message.pv.length === 0) {
      return;
    }

    pending.principalVariations.set(message.multiPv, {
      multiPv: message.multiPv,
      depth: message.depth,
      nodes: message.nodes,
      score: message.score,
      moves: Object.freeze([...message.pv]),
    });
  }

  private handleMalformedOutput(reason: string, raw: string): void {
    const error = new StockfishClientError(
      'malformed-output',
      `Malformed Stockfish output (${reason}): ${raw}`,
    );
    this.handleWorkerFailure(error, this.workerPhase === 'stopping');
  }

  private handleWorkerFailure(error: StockfishClientError, allowRecovery = true): void {
    const failedCurrent = this.currentSearch;
    this.currentSearch = null;
    this.terminateWorker();

    if (failedCurrent && !failedCurrent.cancelled && !failedCurrent.settled) {
      if (allowRecovery && !failedCurrent.recoveryAttempted) {
        failedCurrent.recoveryAttempted = true;
        failedCurrent.principalVariations.clear();
        if (this.queuedSearch) {
          this.cancelPending(this.queuedSearch, 'superseded');
        }
        this.queuedSearch = failedCurrent;
        this.restartWorkerForPendingSearch();
        return;
      }

      this.rejectPending(failedCurrent, error);
      this.rejectInitialization(error);
      return;
    }

    if (this.queuedSearch) {
      if (allowRecovery && !this.queuedSearch.recoveryAttempted) {
        this.queuedSearch.recoveryAttempted = true;
        this.restartWorkerForPendingSearch();
        return;
      }

      this.rejectPending(this.queuedSearch, error);
      this.queuedSearch = null;
      this.rejectInitialization(error);
      return;
    }

    if (failedCurrent?.cancelled || this.initialization === null) {
      return;
    }

    if (allowRecovery && !this.initializationRecoveryAttempted) {
      this.initializationRecoveryAttempted = true;
      this.startWorker();
      return;
    }

    this.rejectInitialization(error);
  }

  private restartWorkerForPendingSearch(): void {
    if (this.destroyed) {
      return;
    }

    if (this.initialization === null) {
      this.initialization = createDeferred<void>();
      void this.initialization.promise.catch(() => undefined);
    }
    this.startWorker();
  }

  private postCommands(commands: readonly string[]): void {
    const worker = this.worker;
    if (!worker) {
      return;
    }

    try {
      for (const command of commands) {
        if (this.worker !== worker) {
          return;
        }
        worker.postMessage(command);
      }
    } catch (error) {
      this.handleWorkerFailure(
        new StockfishClientError('worker-error', 'Posting a UCI command failed.', { cause: error }),
      );
    }
  }

  private startHandshakeTimer(): void {
    this.clearHandshakeTimer();
    this.handshakeTimer = globalThis.setTimeout(() => {
      this.handleWorkerFailure(
        new StockfishClientError('timeout', 'Stockfish UCI handshake timed out.'),
      );
    }, this.handshakeTimeoutMs);
  }

  private startSearchTimer(): void {
    this.clearSearchTimer();
    this.searchTimer = globalThis.setTimeout(() => {
      this.handleWorkerFailure(
        new StockfishClientError('timeout', 'Stockfish search timed out.'),
      );
    }, this.searchTimeoutMs);
  }

  private startStopTimer(): void {
    this.clearStopTimer();
    this.stopTimer = globalThis.setTimeout(() => {
      const hasReplacement = this.queuedSearch !== null;
      const error = new StockfishClientError('timeout', 'Stockfish stop watchdog timed out.');
      if (hasReplacement) {
        this.handleWorkerFailure(error);
      } else {
        this.currentSearch = null;
        this.terminateWorker();
      }
    }, this.stopTimeoutMs);
  }

  private clearHandshakeTimer(): void {
    if (this.handshakeTimer !== null) {
      globalThis.clearTimeout(this.handshakeTimer);
      this.handshakeTimer = null;
    }
  }

  private clearSearchTimer(): void {
    if (this.searchTimer !== null) {
      globalThis.clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
  }

  private clearStopTimer(): void {
    if (this.stopTimer !== null) {
      globalThis.clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }
  }

  private terminateWorker(): void {
    this.clearHandshakeTimer();
    this.clearSearchTimer();
    this.clearStopTimer();
    const worker = this.worker;
    this.worker = null;
    this.workerPhase = 'none';
    this.parser.reset();

    if (worker) {
      worker.onmessage = null;
      worker.onerror = null;
      worker.terminate();
    }
  }

  private cancelPending(
    pending: PendingSearch,
    reason: StockfishCancelledResult['reason'],
  ): void {
    pending.cancelled = true;
    if (pending.settled) {
      return;
    }

    const result: StockfishCancelledResult = {
      ...pending.request,
      status: 'cancelled',
      reason,
    };
    pending.settled = true;
    pending.deferred.resolve(result);
  }

  private rejectPending(pending: PendingSearch, error: StockfishClientError): void {
    if (pending.settled) {
      return;
    }
    pending.settled = true;
    pending.deferred.reject(error);
  }

  private rejectInitialization(error: StockfishClientError): void {
    const initialization = this.initialization;
    this.initialization = null;
    initialization?.reject(error);
  }
}

/** Creates a factory for the vendored classic worker without constructing it at import time. */
export function createClassicStockfishWorkerFactory(
  scriptUrl: string | URL,
): StockfishWorkerFactory {
  return () => {
    const browserWorker = new Worker(scriptUrl, {
      name: 'capychess-stockfish',
      type: 'classic',
    });
    const adapter: StockfishWorkerLike = {
      onmessage: null,
      onerror: null,
      postMessage(message) {
        browserWorker.postMessage(message);
      },
      terminate() {
        browserWorker.onmessage = null;
        browserWorker.onerror = null;
        browserWorker.terminate();
      },
    };

    browserWorker.onmessage = (event) => adapter.onmessage?.({ data: event.data });
    browserWorker.onerror = (event) => adapter.onerror?.({
      message: event.message,
      error: event.error,
    });
    return adapter;
  };
}

function createDeferred<T>(): Deferred<T> {
  let resolve!: Deferred<T>['resolve'];
  let reject!: Deferred<T>['reject'];
  const promise = new Promise<T>((promiseResolve, promiseReject) => {
    resolve = promiseResolve;
    reject = promiseReject;
  });

  return { promise, resolve, reject };
}

function createPendingSearch(request: StockfishSearchRequest): PendingSearch {
  return {
    request: Object.freeze({ ...request }),
    deferred: createDeferred<StockfishSearchOutcome>(),
    principalVariations: new Map(),
    settled: false,
    cancelled: false,
    recoveryAttempted: false,
  };
}

function validateSearchRequest(
  request: StockfishSearchRequest,
  lastSearchId: number,
): StockfishSearchRequest {
  if (!isNonNegativeSafeInteger(request.searchId) || request.searchId <= lastSearchId) {
    throw new StockfishClientError(
      'invalid-request',
      `searchId must be a non-negative, strictly increasing safe integer (last: ${lastSearchId}).`,
    );
  }
  if (!isNonNegativeSafeInteger(request.gameEpoch)) {
    throw new StockfishClientError('invalid-request', 'gameEpoch must be a non-negative safe integer.');
  }
  if (!isNonNegativeSafeInteger(request.positionRevision)) {
    throw new StockfishClientError(
      'invalid-request',
      'positionRevision must be a non-negative safe integer.',
    );
  }
  const canonicalFen = canonicalizeFen(request.fen);
  if (
    request.nodes === undefined
    && request.depth === undefined
    && request.moveTimeMs === undefined
  ) {
    throw new StockfishClientError(
      'invalid-request',
      'A nodes, depth, or move-time limit is required.',
    );
  }
  for (const [name, value] of [
    ['nodes', request.nodes],
    ['depth', request.depth],
    ['moveTimeMs', request.moveTimeMs],
    ['multiPv', request.multiPv],
  ] as const) {
    if (value !== undefined && (!Number.isSafeInteger(value) || value <= 0)) {
      throw new StockfishClientError('invalid-request', `${name} must be a positive safe integer.`);
    }
  }

  return Object.freeze({ ...request, fen: canonicalFen });
}

function canonicalizeFen(fen: string): string {
  if (fen.length === 0 || fen !== fen.trim() || /\s{2,}|[\r\n\t]/.test(fen)) {
    throw new StockfishClientError(
      'invalid-request',
      'fen must be canonical single-line text without surrounding or repeated whitespace.',
    );
  }
  if (fen.split(' ').length !== 6) {
    throw new StockfishClientError('invalid-request', 'fen must contain exactly six fields.');
  }

  try {
    return new Chess(fen).fen();
  } catch (error) {
    throw new StockfishClientError('invalid-request', 'fen is not a legal chess position.', {
      cause: error,
    });
  }
}

function isNonNegativeSafeInteger(value: number): boolean {
  return Number.isSafeInteger(value) && value >= 0;
}

function positiveTimeout(value: number | undefined, fallback: number, name: string): number {
  const timeout = value ?? fallback;
  if (!Number.isFinite(timeout) || timeout <= 0) {
    throw new StockfishClientError('invalid-request', `${name} must be greater than zero.`);
  }
  return timeout;
}

function positiveSafeInteger(value: number | undefined, fallback: number, name: string): number {
  const integer = value ?? fallback;
  if (!Number.isSafeInteger(integer) || integer <= 0) {
    throw new StockfishClientError('invalid-request', `${name} must be a positive safe integer.`);
  }
  return integer;
}
